import {getActionStatusIcon} from './action-status-icon.ts';
import type {ActionsStatus} from './gitea-actions.ts';
import {svgParseOuterInner} from '../svg.ts';
import {html, htmlRaw} from '../utils/html.ts';

const {svgOuter, svgInnerHtml: giteaFaviconInner} = svgParseOuterInner('gitea-favicon');
const faviconViewBox = svgOuter.getAttribute('viewBox')!;
const [faviconViewBoxX, faviconViewBoxY, faviconViewBoxWidth, faviconViewBoxHeight] = faviconViewBox.split(/\s+/).map(Number);

const badgeIconSize = 16;
const badgeMargin = faviconViewBoxWidth * 0.05;

let currentStatus: ActionsStatus | null = null;
const defaultFaviconHrefs = new Map<HTMLLinkElement, {href: string, type: string}>();
const faviconDataUrlCache = new Map<ActionsStatus, string>();
let colorProbe: HTMLElement | null = null;

function rememberDefaultFaviconHrefs() {
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')) {
    if (!defaultFaviconHrefs.has(link)) defaultFaviconHrefs.set(link, {href: link.href, type: link.type});
  }
}

function resolveTailwindTextColor(colorClass: string): string {
  if (!colorProbe) {
    colorProbe = document.createElement('span');
    colorProbe.style.display = 'none';
    document.body.append(colorProbe);
  }
  colorProbe.className = colorClass;
  return getComputedStyle(colorProbe).color || '#000000';
}

function buildStatusIconMarkup(status: ActionsStatus): string {
  const badgeDrawSize = faviconViewBoxWidth * 0.45;
  const badgeX = faviconViewBoxX + faviconViewBoxWidth - badgeDrawSize - badgeMargin;
  const badgeY = faviconViewBoxY + faviconViewBoxHeight - badgeDrawSize - badgeMargin;
  const badgeScale = badgeDrawSize / badgeIconSize;
  const badgeCenter = badgeDrawSize / 2;
  const badgeRingRadius = badgeCenter + badgeDrawSize * 0.08;
  const {name, colorClass} = status === 'running' || status === 'pending' || status === 'waiting' ?
    {name: 'octicon-circle' as const, colorClass: 'tw-text-orange'} :
    getActionStatusIcon(status, 'circle-fill');
  const color = resolveTailwindTextColor(colorClass);
  const {svgInnerHtml} = svgParseOuterInner(name);
  const coloredInner = svgInnerHtml.replaceAll('currentColor', () => color);
  const ring = html`<circle cx="${badgeX + badgeCenter}" cy="${badgeY + badgeCenter}" r="${badgeRingRadius}" fill="#ffffff"/>`;
  const badge = html`<g data-actions-status-name="${status}" transform="translate(${badgeX}, ${badgeY}) scale(${badgeScale})" fill="${color}" color="${color}">${htmlRaw(coloredInner)}</g>`;
  return html`${htmlRaw(ring)}${htmlRaw(badge)}`;
}

export function buildStatusFaviconSvg(status: ActionsStatus): string {
  return html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${faviconViewBox}">${htmlRaw(giteaFaviconInner)}${htmlRaw(buildStatusIconMarkup(status))}</svg>`;
}

function buildStatusFaviconDataUrl(status: ActionsStatus): string {
  const cached = faviconDataUrlCache.get(status);
  if (cached) return cached;
  const dataUrl = `data:image/svg+xml,${encodeURIComponent(buildStatusFaviconSvg(status))}`;
  faviconDataUrlCache.set(status, dataUrl);
  return dataUrl;
}

function setFaviconHref(href: string) {
  rememberDefaultFaviconHrefs();
  for (const link of defaultFaviconHrefs.keys()) {
    if (link.isConnected) {
      link.type = 'image/svg+xml';
      if (link.href !== href) link.href = href;
    }
  }
}

export function syncActionRunFavicon(status: ActionsStatus | ''): void {
  if (status === '') {
    resetActionFavicon();
    return;
  }
  setFaviconHref(buildStatusFaviconDataUrl(status));
  currentStatus = status;
}

export function resetActionFavicon(): void {
  if (currentStatus === null) return;
  rememberDefaultFaviconHrefs();
  for (const [link, original] of defaultFaviconHrefs) {
    if (link.isConnected) {
      link.type = original.type;
      link.href = original.href;
    }
  }
  currentStatus = null;
  defaultFaviconHrefs.clear();
}

export function syncPullRequestFavicon(root: ParentNode): void {
  const counts = root.querySelector('[data-pull-check-statuses]')?.getAttribute('data-pull-check-statuses');
  const items: Array<{Status: string}> = (counts && JSON.parse(counts)) || [];
  const statuses = new Set(items.map((item) => item.Status));
  let status: ActionsStatus | '' = '';
  if (statuses.has('failed')) {
    status = 'failure';
  } else if (['running', 'queued', 'waiting', 'blocked', 'missing'].some((state) => statuses.has(state))) {
    status = 'running';
  } else if (statuses.has('successful') && !statuses.has('canceled') && !statuses.has('unknown')) {
    status = 'success';
  }
  syncActionRunFavicon(status);
}
